import type { ActorRef, ActorRefFrom, Snapshot } from 'xstate';
import { assign, fromCallback, sendTo, setup } from 'xstate';

import { loadTimer, markTimerExpired, removeTimer, saveTimer } from './lib';
import { SurveyState } from './states';

export enum TimerState {
    STOPPED = 'stopped',
    RUNNING = 'running',
    EXPIRED = 'expired',
    ERROR = 'error',
}

export type TimerStopReason = 'expired' | 'next-bundle';

export type TimerEvents = { type: 'START' } | { type: 'STOP' } | { type: 'EXPIRED' } | { type: 'TICK' };

export type TimerContext = {
    name: string;
    uniqueId: string;
    startTime: Date;
    duration: number; // in seconds
    interval: number; // in ms
    timeAgo: number; // in ms
    parentRef?: ParentActor;
    stopReason?: TimerStopReason;
};

export type TimerType = {
    value: TimerState;
    context: TimerContext;
};

export const initialTimerContext: Readonly<TimerContext> = {
    name: 'timer_machine',
    uniqueId: '',
    duration: null,
    interval: 1000,
    timeAgo: 0,
    startTime: new Date(),
};

const timerExpired = ({ context: { duration, startTime } }: { context: TimerContext }): boolean => {
    const endTime = new Date(startTime.getTime() + duration * 1000);
    return Number(endTime) <= Date.now() || duration === null || duration <= 0;
};

type ChildEvent = { type: 'TIMER_EXPIRED'; reason: TimerStopReason };

type ParentActor = ActorRef<Snapshot<unknown>, ChildEvent>;

export const TimerMachine = setup({
    types: {
        context: {} as TimerContext,
        events: {} as TimerEvents,
        input: {} as { duration: number; parentRef: ParentActor; uniqueId: string },
    },
    actors: {
        ticker: fromCallback<{ type: 'TICK' }, { interval: number }>(({ input, sendBack }) => {
            sendBack({ type: 'TICK' });
            const interval = setInterval(() => {
                sendBack({ type: 'TICK' });
            }, input.interval);
            return () => {
                clearInterval(interval);
            };
        }),
    },
}).createMachine({
    initial: TimerState.STOPPED,
    schema: {
        context: {} as TimerContext,
        events: {} as TimerEvents,
    },
    context: ({ input }) => {
        const savedValue = loadTimer(input.uniqueId);
        // Время старта сохраняется только на реальный START (см. переходы ниже).
        // Если сохранить его здесь, то никогда не запущенный таймер оставляет ключ,
        // и после перезагрузки страницы (мобильные браузеры выгружают вкладки)
        // он выглядит так, будто всё это время «тикал в фоне».
        const startTime = new Date();
        return {
            ...initialTimerContext,
            uniqueId: input.uniqueId,
            startTime: savedValue ? new Date(savedValue) : startTime,
            duration: input.duration,
            timeAgo: savedValue ? Date.now() - savedValue : 0,
            parentRef: input.parentRef,
        };
    },
    states: {
        [TimerState.STOPPED]: {
            on: {
                START: {
                    target: TimerState.RUNNING,
                    actions: assign(({ context }) => {
                        const prevTimeAgo = context.timeAgo;
                        const newStartTime = Date.now() - prevTimeAgo;
                        saveTimer(context.uniqueId, newStartTime);
                        return {
                            startTime: new Date(newStartTime),
                        };
                    }),
                },
            },
        },
        [TimerState.RUNNING]: {
            invoke: {
                src: 'ticker',
                input: ({ context }) => ({ interval: context.interval }),
                onError: {
                    target: SurveyState.error,
                },
            },
            on: {
                TICK: [
                    {
                        target: TimerState.EXPIRED,
                        guard: timerExpired,
                        actions: assign({
                            timeAgo: ({ context }) => {
                                return Math.min(Date.now() - context.startTime.getTime(), context.duration * 1000);
                            },
                            stopReason: 'expired',
                        }),
                    },
                    {
                        actions: [
                            assign(({ context }) => {
                                return {
                                    timeAgo: Math.min(
                                        Date.now() - context.startTime.getTime(),
                                        context.duration * 1000,
                                    ),
                                };
                            }),
                        ],
                    },
                ],
                STOP: {
                    target: TimerState.EXPIRED,
                    actions: assign({
                        timeAgo: ({ context }) =>
                            Math.min(Date.now() - context.startTime.getTime(), context.duration * 1000),
                        stopReason: 'next-bundle',
                    }),
                },
            },
        },
        [TimerState.EXPIRED]: {
            entry: [
                // Истёкший по времени таймер больше не нужен в localStorage: убираем
                // ключ, чтобы при новой попытке/перезагрузке таймер не восстанавливал
                // старое (уже истёкшее) время старта. При STOP по переходу на следующий
                // бандл ('next-bundle') ключ сохраняем — по нему восстанавливается
                // оставшееся время, если пользователь вернётся на вопрос.
                // Факт истечения запоминаем маркером: по нему навигация «назад»
                // не пускает на вопрос с истёкшим таймером даже после перезагрузки.
                ({ context }) => {
                    if (context.stopReason === 'expired') {
                        removeTimer(context.uniqueId);
                        markTimerExpired(context.uniqueId);
                    }
                },
                sendTo(
                    ({ context }) => context.parentRef,
                    ({ context: { stopReason } }) => {
                        return {
                            type: 'TIMER_EXPIRED',
                            reason: stopReason,
                        };
                    },
                ),
            ],
            on: {
                START: {
                    target: TimerState.RUNNING,
                    actions: assign(({ context }) => {
                        const prevTimeAgo = context.timeAgo;
                        const newStartTime = Date.now() - prevTimeAgo;
                        saveTimer(context.uniqueId, newStartTime);
                        return {
                            startTime: new Date(newStartTime),
                        };
                    }),
                },
            },
        },
        [TimerState.ERROR]: {
            // entry: assign((ctx, evt) => {
            //     // TODO HANDLE ERROR
            // })
        },
    },
});

export type TimerMachineActor = ActorRefFrom<typeof TimerMachine>;
